import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, Image } from 'react-bootstrap';
import { useBookingState, useAccountState, useVehicleAssignmentsState } from '../../../../Context';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import './index.scss';
import { InfoField } from '../../../../Components/Reusable';
import MockupLayout from '../../../../Components/MockupLayout';
import CustomAccordion from '../../../../Components/CustomAccordion';
import { formatDateTimeWithDayAbbr } from '../../../../utils/helper';

dayjs.extend(utc);
dayjs.extend(timezone);

const SalespersonCard = ({ bookingDetails }) => {
    return (
        <>
            <div className="salesperson-img-container">
                <Image
                    src={bookingDetails.salesPersonPhoto || 'https://placehold.co/150?text=Salesperson'}
                    rounded
                    className="img-cover"
                />
            </div>
            <div className="ml-3 d-flex flex-column justify-content-between w-100" style={{ height: '170px' }}>
                <div>
                    <h4 className="mb-0 font-weight-bold text-dark">{bookingDetails.salesPersonFullName}</h4>
                    <div className="text-muted mt-1" style={{ fontWeight: '500' }}>
                        Salesperson
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <a
                        href={`mailto:${bookingDetails.salesPersonEmail}`}
                        className="text-dark mr-4"
                        title="Email Salesperson"
                    >
                        <i className="far fa-envelope fa-2x"></i>
                    </a>
                    <a href={`tel:${bookingDetails.salesPersonPhone}`} className="text-dark" title="Call Salesperson">
                        <i className="fas fa-phone-alt fa-2x"></i>
                    </a>
                </div>
            </div>
        </>
    );
};

const DriverCard = ({ vehicle, tripSettings }) => {
    // Show photo only if enabled

    const { cp_showDriverFirstName, cp_showDriverLastName, cp_showDriverPhoneNumber, cp_showDriverPhoto } =
        tripSettings || {};

    const photoUrl =
        cp_showDriverPhoto && vehicle.driverPhoto ? vehicle.driverPhoto : 'https://placehold.co/150?text=Driver';

    const firstName = cp_showDriverFirstName ? (vehicle?.driverFirstName ?? '') : '';
    const lastName = cp_showDriverLastName ? (vehicle?.driverLastName ?? '') : '';
    const showPhone = cp_showDriverPhoneNumber && vehicle?.mobilePhone;

    return (
        <Card className="h-100 shadow-sm" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
            <Card.Body className="d-flex p-2">
                {/* Driver Photo */}
                {!!cp_showDriverPhoto && (
                    <div className="driver-img-container">
                        <Image src={photoUrl} rounded className="img-cover" />
                    </div>
                )}

                <div className="ml-3 d-flex flex-column justify-content-between w-100" style={{ height: '140px' }}>
                    <div>
                        <h5 className="mb-0 font-weight-bold text-dark">
                            {firstName} {lastName}
                        </h5>

                        <div className="text-primary mt-1" style={{ fontWeight: '500' }}>
                            Driver
                        </div>
                    </div>

                    {/* Phone */}
                    <div className="d-flex align-items-center">
                        {!!showPhone && (
                            <a href={`tel:${vehicle.mobilePhone}`} className="mr-2 text-dark" title="Call Driver">
                                <i className="fas fa-phone-alt fa-lg"></i>
                            </a>
                        )}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export const amenitiesConfig = [
    { key: 'wifiEnabled', icon: 'fas fa-wifi', title: 'WiFi' },
    { key: 'powerOutlets', icon: 'fas fa-plug', title: 'Power Outlets' },
    { key: 'handicapAccessible', icon: 'fas fa-wheelchair', title: 'ADA Accessible' },
    { key: 'seatbelts', icon: 'fas fa-chair', title: 'Seatbelts' },
    { key: 'dvdPlayer', icon: 'fas fa-compact-disc', title: 'DVD Player' },
    { key: 'lavatory', icon: 'fas fa-restroom', title: 'Lavatory' },
    { key: 'sateliteTV', icon: 'fas fa-tv', title: 'Satellite TV' },
    { key: 'trayTables', icon: 'fas fa-utensils', title: 'Tray Tables' },
    { key: 'luggageSpace', icon: 'fas fa-suitcase', title: 'Luggage Space' },
    { key: 'driverProtectiveShield', icon: 'fas fa-user-shield', title: 'Driver Protective Shield' },
];

const VehicleCard = ({ vehicle, tripSettings }) => {
    const { cp_showVehicleExteriorPhoto, cp_showVehicleInteriorPhoto, cp_showVehicleType, cp_showVehicleId } =
        tripSettings || {};

    const hasExteriorPhoto = cp_showVehicleExteriorPhoto && vehicle.vehicleExteriorPhoto;

    const hasInteriorPhoto = cp_showVehicleInteriorPhoto && vehicle.vehicleInteriorPhoto;

    const fallbackPhoto = 'https://placehold.co/200x120?text=Vehicle';

    // Create array of available photos
    const photos = [];
    if (hasExteriorPhoto) {
        photos.push({ url: vehicle.vehicleExteriorPhoto, type: 'Exterior' });
    }
    if (hasInteriorPhoto) {
        photos.push({ url: vehicle.vehicleInteriorPhoto, type: 'Interior' });
    }
    if (photos.length === 0) {
        photos.push({ url: fallbackPhoto, type: 'Vehicle' });
    }

    const carouselId = `vehicle-carousel-${vehicle.id || Math.random()}`;

    return (
        <Card className="h-100 shadow-sm" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
            <Card.Body className="p-0 d-flex">
                <div className="vehicle-carousel-container">
                    {photos.length > 1 ? (
                        <div id={carouselId} className="carousel slide vehicle-carousel h-100" data-ride="carousel">
                            <div className="carousel-inner h-100">
                                {photos.map((photo, index) => (
                                    <div key={index} className={`carousel-item h-100 ${index === 0 ? 'active' : ''}`}>
                                        <Image src={photo.url} className="vehicle-img d-block w-100" alt={photo.type} />
                                        <div className="carousel-caption d-none d-md-block">
                                            <small>{photo.type}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <a
                                className="carousel-control-prev"
                                href={`#${carouselId}`}
                                role="button"
                                data-slide="prev"
                            >
                                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                <span className="sr-only">Previous</span>
                            </a>
                            <a
                                className="carousel-control-next"
                                href={`#${carouselId}`}
                                role="button"
                                data-slide="next"
                            >
                                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                <span className="sr-only">Next</span>
                            </a>
                        </div>
                    ) : (
                        <Image src={photos[0].url} className="vehicle-img" alt={photos[0].type} />
                    )}
                </div>

                <div className="p-3 w-100 d-flex flex-column justify-content-between">
                    <div>
                        {!!cp_showVehicleType && <h6 className="font-weight-bold mb-1">{vehicle.vehicleType}</h6>}

                        <small className="text-muted d-block mb-2">Seats up to {vehicle.seats || 'N/A'}</small>

                        <div className="mb-2 text-muted d-flex flex-wrap">
                            {amenitiesConfig.map((item) =>
                                vehicle[item.key] ? (
                                    <i
                                        key={item.key}
                                        className={`${item.icon} mr-2 mb-1`}
                                        title={item.title}
                                        style={{ cursor: 'help' }}
                                    ></i>
                                ) : null
                            )}
                        </div>
                    </div>

                    {/* TODO: Implement more info modal */}
                    {/* <small className="font-weight-bold cursor-pointer" style={{ color: '#eb592a' }}>
                        <i className="fas fa-info-circle mr-1"></i> More Info
                    </small> */}
                </div>
            </Card.Body>
        </Card>
    );
};

// TODO: Implement tracking card
const TrackingCard = () => {
    return (
        <Card className="h-100 shadow-sm overflow-hidden tracking-card">
            <div className="tracking-map-container">
                <div className="tracking-map-bg"></div>

                <div className="tracking-badge-pos">
                    <span className="badge badge-dark p-2" style={{ fontSize: '12px' }}>
                        VEHICLE TRACKING
                    </span>
                </div>
                <div className="tracking-expand-pos">
                    <i className="fas fa-expand fa-lg text-dark bg-white p-1 rounded"></i>
                </div>
            </div>
        </Card>
    );
};

const BookingDriverDetails = (props) => {
    const bookingId = props.match.params.id;
    const isAuthenticated = Boolean(localStorage.getItem('token'));
    const [collapseItem, handleCollapse] = useState({ 1: true, 2: true, 3: true });
    const [showAllRoutes, setShowAllRoutes] = useState(false);
    const [selectedRouteId, setSelectedRouteId] = useState(null);

    const {
        bookings: { bookingDetails, vehicles },
    } = useBookingState();
    const {
        tripSettings: { tripSettings },
    } = useAccountState();
    const {
        vehicleAssignments: { vehicleAssignments },
    } = useVehicleAssignmentsState();

    useEffect(() => {
        if (vehicleAssignments && vehicleAssignments.length > 0 && !selectedRouteId) {
            setSelectedRouteId(vehicleAssignments[0].id);
        }
    }, [vehicleAssignments, selectedRouteId]);

    const handleItemCollapse = (e) => {
        const { id } = e.target;
        handleCollapse((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleRouteSelect = (id) => {
        setSelectedRouteId((prev) => (prev === id ? null : id));
    };

    return (
        <MockupLayout
            title={`Booking ${bookingDetails?.bookingId || 'Details'} ${bookingDetails.cancelledAt ? '(Cancelled)' : ''}`}
            titleIconClass={'fas fa-calendar-check'}
            textColor={tripSettings?.textColor}
            buttons={
                isAuthenticated && (
                    <Button
                        onClick={() => props.history.push(`/bookings/${bookingId}`)}
                        style={{ backgroundColor: `${tripSettings.buttonColor}`, borderColor: 'transparent' }}
                    >
                        BACK TO BOOKING DETAILS
                    </Button>
                )
            }
        >
            {/* --- TRIP DETAILS SECTION --- */}
            <CustomAccordion
                id={1}
                title="TRIP DETAILS"
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                className="px-2"
            >
                <Row className="mx-1 py-2">
                    <Col md={3} className="d-flex align-items-start border-right">
                        <SalespersonCard bookingDetails={bookingDetails} />
                    </Col>

                    <Col md={9}>
                        <Row>
                            <Col>
                                <InfoField
                                    label="Trip Reference"
                                    colClass="col-md-11"
                                    value={bookingDetails.tripReference}
                                />
                                <div className="mt-3">
                                    <InfoField
                                        label="Group Name"
                                        colClass="col-md-11"
                                        value={bookingDetails.groupName}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <InfoField
                                    label="Purchase Order #"
                                    colClass="col-md-11"
                                    value={bookingDetails.purchaseOrder}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </CustomAccordion>

            {/* --- ROUTE SELECTION --- */}
            {vehicleAssignments.length > 1 && (
                <CustomAccordion
                    id={2}
                    title="ROUTE INFORMATION"
                    collapseItem={collapseItem}
                    handleItemCollapse={handleItemCollapse}
                    sectionColor="#999"
                >
                    <div className={`p-3 route-information route-scroll-container ${showAllRoutes ? 'expanded' : ''}`}>
                        <Row>
                            {(showAllRoutes || vehicleAssignments.length <= 12
                                ? vehicleAssignments
                                : vehicleAssignments.slice(0, 11)
                            ).map((assignment) => (
                                <Col md={4} className="mb-2" key={assignment.id}>
                                    <Button
                                        variant="outline-primary"
                                        className="w-100 text-truncate"
                                        onClick={() => handleRouteSelect(assignment.id)}
                                        active={selectedRouteId === assignment.id}
                                        title={assignment.routeDescription}
                                        style={{ borderWidth: '2px' }}
                                    >
                                        {assignment.routeDescription || 'ROUTE DESCRIPTION'}
                                    </Button>
                                </Col>
                            ))}

                            {!showAllRoutes && vehicleAssignments.length > 12 && (
                                <Col md={4} className="mb-2">
                                    <Button
                                        variant="outline-primary"
                                        className="w-100 font-weight-bold"
                                        onClick={() => setShowAllRoutes(true)}
                                        style={{ borderWidth: '2px', borderStyle: 'dashed' }}
                                    >
                                        MORE ROUTES...
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </div>
                </CustomAccordion>
            )}

            {/* --- DISPATCH & DRIVER INFO --- */}
            {vehicleAssignments.map((assignment) => {
                if (selectedRouteId !== assignment.id) return null;

                const assignmentVehicles = vehicles.filter((v) => v.vehicleAssignmentId === assignment.id);

                return (
                    <div key={assignment.id}>
                        {/* Dispatch Information */}
                        <CustomAccordion
                            id={`dispatch-${assignment.id}`}
                            title="DISPATCH INFORMATION"
                            collapseItem={{ [`dispatch-${assignment.id}`]: true }}
                            handleItemCollapse={() => {}}
                            sectionColor="#999"
                        >
                            <div className="p-2">
                                <Row className="mx-1 bg-light p-3 rounded border mb-2">
                                    <InfoField
                                        label="Route Description"
                                        value={assignment.routeDescription}
                                        colClass="col-12 col-md-8"
                                    />
                                    <InfoField
                                        label="First Pick-Up Time"
                                        value={formatDateTimeWithDayAbbr(assignment.firstDepartureTime)}
                                        colClass="col-12 col-md-4"
                                    />
                                    <InfoField
                                        label="First Pick-Up Location"
                                        value={assignment.firstPickupLocation}
                                        colClass="col-12 col-md-6"
                                    />
                                    <InfoField
                                        label="Destination"
                                        value={assignment.finalDropLocation}
                                        colClass="col-12 col-md-6"
                                    />
                                </Row>
                            </div>
                        </CustomAccordion>

                        {/* Drivers & Vehicles */}
                        <CustomAccordion
                            id={`drivers-${assignment.id}`}
                            title="DRIVER(S) & VEHICLE(S)"
                            collapseItem={{ [`drivers-${assignment.id}`]: true }}
                            handleItemCollapse={() => {}}
                            sectionColor="#999"
                        >
                            <div className="p-3">
                                {assignmentVehicles.length === 0 && (
                                    <div className="text-center p-3 text-muted">
                                        No drivers or vehicles assigned to this route yet.
                                    </div>
                                )}
                                {assignmentVehicles.length > 0 &&
                                    assignmentVehicles.map((vehicle, idx) => {
                                        const {
                                            cp_showDriverFirstName,
                                            cp_showDriverLastName,
                                            cp_showDriverPhoneNumber,
                                            cp_showDriverPhoto,
                                            cp_showVehicleType,
                                            cp_showVehicleId,
                                            cp_showVehicleExteriorPhoto,
                                            cp_showVehicleInteriorPhoto,
                                        } = tripSettings || {};

                                        const is_driverAssigned = vehicle?.driverID;
                                        const is_vehicleAssigned = vehicle?.vehicleID;

                                        const hasDriverInfo =
                                            !!(
                                                cp_showDriverFirstName ||
                                                cp_showDriverLastName ||
                                                cp_showDriverPhoneNumber ||
                                                cp_showDriverPhoto
                                            ) && !!is_driverAssigned;
                                        const hasVehicleInfo =
                                            !!(
                                                cp_showVehicleType ||
                                                cp_showVehicleId ||
                                                cp_showVehicleExteriorPhoto ||
                                                cp_showVehicleInteriorPhoto
                                            ) && !!is_vehicleAssigned;

                                        return (
                                            <Row key={vehicle.id || idx} className="mb-3">
                                                {/* DRIVER COLUMN */}
                                                <Col lg={3} md={4} className="mb-2">
                                                    {hasDriverInfo ? (
                                                        <DriverCard vehicle={vehicle} tripSettings={tripSettings} />
                                                    ) : (
                                                        <Card
                                                            className="h-100 shadow-sm d-flex justify-content-center align-items-center bg-light"
                                                            style={{
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: '8px',
                                                                minHeight: '158px',
                                                            }}
                                                        >
                                                            <div className="text-muted font-weight-bold text-center">
                                                                <i
                                                                    className={`fas ${is_driverAssigned ? 'fa-user-check' : 'fa-user-clock'} fa-2x mb-2 d-block`}
                                                                ></i>
                                                                {is_driverAssigned
                                                                    ? 'Driver Assigned'
                                                                    : 'No Driver Assigned'}
                                                            </div>
                                                        </Card>
                                                    )}
                                                </Col>

                                                {/* VEHICLE COLUMN */}
                                                <Col lg={5} md={8} className="mb-2">
                                                    {hasVehicleInfo ? (
                                                        <VehicleCard vehicle={vehicle} tripSettings={tripSettings} />
                                                    ) : (
                                                        <Card
                                                            className="h-100 shadow-sm d-flex justify-content-center align-items-center bg-light"
                                                            style={{
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: '8px',
                                                                minHeight: '158px',
                                                            }}
                                                        >
                                                            <div className="text-muted font-weight-bold text-center">
                                                                <i
                                                                    className={`fas ${is_vehicleAssigned ? 'fa-bus' : 'fa-clock'} fa-2x mb-2 d-block`}
                                                                ></i>
                                                                {is_vehicleAssigned
                                                                    ? 'Vehicle Assigned'
                                                                    : 'No Vehicle Assigned'}
                                                            </div>
                                                        </Card>
                                                    )}
                                                </Col>

                                                {/* TRACKING COLUMN */}
                                                <Col lg={4} md={12} className="mb-2">
                                                    {/* We should not include any logic or visuals towards Vehicle Tracking at the moment.  */}
                                                    {/* <TrackingCard /> */}
                                                </Col>
                                            </Row>
                                        );
                                    })}
                            </div>
                        </CustomAccordion>
                    </div>
                );
            })}
        </MockupLayout>
    );
};

export default BookingDriverDetails;
